import { Box, Drawer, Modal, Tooltip } from '@mui/material'
import { useCallback, useEffect, useState } from 'react'
import { IApprovalRequest } from '../../definitions/Approval'
import { IFlow, INode } from '../../definitions/Nodes'
import { IExecutionEnvironment, ISimpleExecutionEnvironment } from '../../definitions/NodesExecution'
import EventsEngine from '../../engines/EventsEngine'
import { IFlowChange } from '../../managers/FlowUIManager'
import ValidationManager from '../../managers/ValidationManager'
import NodeAuditPanel from './node_audit_panel/NodeAuditPanel'
import NodeComparePanel from './NodeComparePanel'
import NodeDataEditor from './NodeDataEditor'
import { FlowEditorMode } from './SettlementFlowEditor'

interface P {
    flow: IFlow
    isLoading: boolean
    applyFlowChange: (change: IFlowChange) => void
    editorMode: FlowEditorMode
    comparedExecEnv?: ISimpleExecutionEnvironment
    execEnv?: IExecutionEnvironment
    approval: IApprovalRequest
}

function NodeDetailPanel({ flow, isLoading, applyFlowChange, editorMode, comparedExecEnv, execEnv, approval }: P) {
    const [open, setOpen] = useState(false)
    const [nodeErrors, setNodeErrors] = useState<string[]>([])
    const [selected, setSelected] = useState<INode>(null)

    const [drawerWidth, setDrawerWidth] = useState(600)

    useEffect(() => {
        EventsEngine.subscribe<{ uuid: string }>('on-node-doubleclick', 'NodeDetailPanel' + flow.uuid, args => {
            const uuid = args.uuid
            const node = flow.nodes.find(x => x.uuid === uuid)
            if (node) {
                setSelected(node)
                const errors = ValidationManager.validateNode(node)
                setNodeErrors(errors)
                setOpen(true)
            }
        })
        return () => EventsEngine.unsubscribe('on-node-doubleclick', 'NodeDetailPanel' + flow.uuid)
    }, [flow, editorMode])

    const closeDrawer = () => {
        setOpen(false)
        setSelected(null)
    }

    const applyChange = async (change: IFlowChange) => {
        const node = change.flow.nodes.find(x => x.uuid === selected.uuid)
        setSelected(node)
        applyFlowChange(change)
    }

    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        e.preventDefault()
        const startX = e.clientX
        const startWidth = drawerWidth
        let animationFrameId: number

        const onMouseMove = (e: MouseEvent) => {
            if (animationFrameId) cancelAnimationFrame(animationFrameId)
            animationFrameId = requestAnimationFrame(() => {
                const newWidth = Math.min(
                    Math.max(startWidth + (startX - e.clientX), 400),
                    window.innerWidth * 0.9
                )
                setDrawerWidth(newWidth)
            })
        }
        const onMouseUp = () => {
            window.removeEventListener('mousemove', onMouseMove)
            window.removeEventListener('mouseup', onMouseUp)
            if (animationFrameId) cancelAnimationFrame(animationFrameId)
        }
        window.addEventListener('mousemove', onMouseMove)
        window.addEventListener('mouseup', onMouseUp)
    }, [drawerWidth])

    if (!selected) return null

    if (editorMode === 'edit') {
        return (
            <Drawer
                open={open}
                anchor='right'
                onClose={closeDrawer}
                PaperProps={{
                    sx: {
                        borderRadius: '12px 0px 0px 12px',
                        width: drawerWidth,
                        minWidth: 400,
                        maxWidth: 'calc(100vw - var(--left-nav-width))'
                    }
                }}>
                <Box
                    sx={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: 8,
                        cursor: 'col-resize',
                        zIndex: 1501
                    }}
                    onMouseDown={handleMouseDown}>
                    <Tooltip title='Drag resize' arrow placement='left' followCursor>
                        <Box sx={{ width: '100%', height: '100%', backgroundColor: 'transparent' }} />
                    </Tooltip>
                </Box>
                <NodeDataEditor
                    flow={flow}
                    applyFlowChange={applyChange}
                    onClose={closeDrawer}
                    isLoading={isLoading}
                    nodeErrors={nodeErrors}
                    setNodeErrors={setNodeErrors}
                    defaultSelected={selected}
                    mode='edit'
                    approval={approval}
                />
            </Drawer>
        )
    } else if (editorMode === 'audit') {
        return (
            <Modal
                open={open}
                onClose={closeDrawer}
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '80vh',
                    marginTop: '5%'
                }}>
                <NodeAuditPanel
                    flow={flow}
                    selectedNode={selected}
                    isLoading
                    nodeErrors={nodeErrors}
                    execEnv={execEnv}
                    onClose={closeDrawer}
                    approval={approval}
                />
            </Modal>
        )
    } else if (editorMode === 'compare') {
        return (
            <Modal
                open={open}
                onClose={closeDrawer}
                sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh', width: 'calc(80vw - var(--unit) * 33)', top: '10%', left: 'calc(var(--unit) * 33 + 10%)' }}>
                <NodeComparePanel
                    comparedExecEnv={comparedExecEnv}
                    selectedNode={selected}
                    execEnv={execEnv}
                />
            </Modal>
        )
    }
}

export default NodeDetailPanel
